<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  selectedCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['assign', 'close']);

const { t } = useI18n();
const store = useStore();

const labels = ref([]);

const availableLabels = useMapGetter('labels/getLabels');

// Fetch labels on component mount
onMounted(async () => {
  try {
    await store.dispatch('labels/get');
  } catch {
    // Silently handle error
  }
});

const toggleLabel = labelTitle => {
  const index = labels.value.indexOf(labelTitle);
  if (index > -1) {
    labels.value.splice(index, 1);
  } else {
    labels.value.push(labelTitle);
  }
};

const removeLabel = index => {
  labels.value.splice(index, 1);
};

const assignLabels = () => {
  if (labels.value.length > 0) {
    emit('assign', labels.value);
  }
};

const close = () => {
  emit('close');
};
</script>

<template>
  <div class="label-actions-popover">
    <div class="label-actions-header">
      <h3>
        {{
          t('BULK_ACTION.LABELS.ASSIGN_LABELS_TO_CONTACTS', {
            count: selectedCount || 0,
          })
        }}
      </h3>
      <NextButton icon="i-lucide-x" variant="ghost" size="xs" @click="close" />
    </div>
    <div class="label-actions-content">
      <div class="existing-labels-section">
        <h4 class="text-sm font-medium text-n-slate-12 mb-2">
          {{ t('BULK_ACTION.LABELS.SELECT_LABELS_TO_ASSIGN') }}
        </h4>
        <div class="existing-labels-list">
          <div
            v-for="label in availableLabels"
            :key="label.id"
            class="existing-label-item"
            :class="{ selected: labels.includes(label.title) }"
            @click="toggleLabel(label.title)"
          >
            <span
              class="label-color"
              :style="{ backgroundColor: label.color }"
            />
            <span class="label-title">{{ label.title }}</span>
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <span v-if="labels.includes(label.title)"
class="selected-icon"
              >✓</span>
          </div>
        </div>
      </div>
      <div v-if="labels.length > 0" class="selected-labels">
        <div
          v-for="(label, index) in labels"
          :key="index"
          class="selected-label"
        >
          <span>{{ label }}</span>
          <NextButton
            icon="i-lucide-x"
            variant="ghost"
            size="xs"
            @click="removeLabel(index)"
          />
        </div>
      </div>
      <div class="label-actions-footer">
        <NextButton
          :label="t('BULK_ACTION.LABELS.ASSIGN')"
          size="sm"
          :disabled="labels.length === 0"
          @click="assignLabels"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.label-actions-popover {
  @apply absolute top-full left-0 mt-2 bg-n-background border border-n-strong rounded-lg shadow-lg z-50 min-w-80;
}

.label-actions-header {
  @apply flex items-center justify-between p-4 border-b border-n-strong;

  h3 {
    @apply text-sm font-medium text-n-slate-12;
  }
}

.label-actions-content {
  @apply p-4 space-y-4;
}

.existing-labels-section {
  @apply mb-4;
}

.existing-labels-list {
  @apply space-y-1 max-h-32 overflow-y-auto;
}

.existing-label-item {
  @apply flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-n-slate-3 transition-colors;
}

.existing-label-item.selected {
  @apply bg-n-slate-3 border border-n-slate-6;
}

.label-color {
  @apply w-3 h-3 rounded-full;
}

.label-title {
  @apply text-sm text-n-slate-12 flex-1;
}

.selected-icon {
  @apply text-n-slate-12 font-bold;
}

.label-input-section {
  @apply flex gap-2;
}

.selected-labels {
  @apply space-y-2;
}

.selected-label {
  @apply flex items-center justify-between p-2 bg-n-slate-3 rounded text-sm;
}

.label-actions-footer {
  @apply flex justify-end pt-2 border-t border-n-strong;
}
</style>
